/**
 * ---------------------------------------------------------------------------
 * IDLE GOVERNOR — stop paying for frames nobody is looking at
 * ---------------------------------------------------------------------------
 *
 * The renderer is unconditional: `Engine.step()` has always ended in
 * `render.render(ctx)`, every rAF tick, from the moment `engine.start()` runs.
 * That is correct while somebody is playing and pure waste the rest of the
 * time, and "the rest of the time" is a lot of wall clock:
 *
 *   - PAUSED. `PauseMenu.show()` sets `time.scale = 0`, so the simulation is
 *     frozen — but the full HDR pipeline (cascades, prepass, GTAO, contact
 *     shadows, SSR, volumetrics, TAA, bloom, DOF, motion blur, composite) still
 *     re-renders the *same* frame at the display refresh rate, indefinitely. A
 *     player who pauses and walks away leaves the GPU pinned on a still image.
 *
 *   - HIDDEN. Another tab is in front, or the window is minimised. Nothing that
 *     is drawn can be seen by anybody.
 *
 * Neither case is a quality question, so this file is deliberately NOT part of
 * the quality ladder in config.js and touches no preset. It removes draw calls
 * whose output is either invisible or bit-identical to the frame already on
 * screen.
 *
 * THE ONE HARD RULE HERE: **the simulation is never touched.** This governor
 * only ever elides a *render* call. `fixedUpdate` (120 Hz physics), `update`,
 * `lateUpdate`, input, networking and AI run exactly as often as they always
 * did, with exactly the same deltas. Nothing about how the game plays, how a
 * bullet traces, or what the server is told can be a function of whether a
 * frame was drawn. That is what makes this safe to have on by default.
 *
 * Modes (`config.idleMode`, chosen in the pause menu):
 *
 *   off          Draw every frame, always. The behaviour before this file.
 *
 *   safe         DEFAULT. Skip only frames that are provably not worth
 *                drawing:
 *                  · the document is hidden — nothing is on screen at all;
 *                  · the game is paused AND the image has settled, so the next
 *                    frame would be identical to the one already presented.
 *                In every state a player can actually see and interact with,
 *                this mode draws exactly the frames `off` draws.
 *
 *   background   `safe`, plus: while the window does not have focus, draw at
 *                BACKGROUND_FPS instead of the refresh rate. The world keeps
 *                simulating at full rate — you can still be shot online while
 *                alt-tabbed — it is only the *drawing* that slows down, so the
 *                GPU stops competing with whatever you switched to. This is the
 *                one mode that changes something a person could see (a window
 *                you are not looking at updates at 10 fps), which is why it is
 *                opt-in rather than the default.
 *
 * WHY "SETTLED" NEEDS FRAMES. A paused frame is not immediately static. TAA
 * walks an 8-frame jitter cycle and keeps folding samples into its history, and
 * the exposure meter is still easing toward its target. Both converge within a
 * few frames of the simulation stopping, but freezing on the first paused frame
 * would freeze a half-converged image. So entering pause buys SETTLE_FRAMES
 * more full frames, and only then does the pipeline go quiet.
 *
 * WAKING. Anything that would make the next frame differ from the presented one
 * has to un-idle: closing the pause menu, a window resize (the canvas is
 * cleared to black by the resize itself), and every setting that moves pixels
 * while the menu is open — FOV, graphics preset, render scale. Those all
 * already announce themselves on the event bus, so `bindEvents` listens rather
 * than requiring their owners to know this file exists.
 */

/** Ordered for the pause-menu segmented control. */
export const IDLE_MODES = ['off', 'safe', 'background'];

/** Where the player's choice is remembered for the next boot (src/main.js). */
export const IDLE_KEY = 'cod:idle';

/** Draw rate for an unfocused window in `background` mode. */
const BACKGROUND_FPS = 10;

/**
 * Slack on the background-mode deadline.
 *
 * rAF only offers frames on the refresh grid, so the deadline is almost never
 * hit exactly: on a 60 Hz display the 100 ms mark falls 0.02 ms *after* the
 * sixth frame, which without slack pushes every draw out to the seventh and
 * paces the window at 8.6 fps, alternating with 6 as the float error drifts. A
 * millisecond of tolerance lets the frame that is essentially on time count as
 * on time, so the rate is a clean divisor of the refresh rate instead of a beat
 * between two of them.
 */
const DEADLINE_SLACK_MS = 1;

/**
 * Full frames drawn after the simulation stops before the pipeline is allowed
 * to go quiet. Covers TAA's 8-frame jitter cycle several times over plus the
 * exposure ease, so what freezes on screen is a converged image.
 */
const SETTLE_FRAMES = 30;

/**
 * Frames drawn after a wake event. One would do for a resize, but a preset
 * change reallocates targets and resets TAA history, which then needs its
 * jitter cycle again before the still image is converged.
 */
const WAKE_FRAMES = 30;

export class IdleGovernor {
  /**
   * @param {object} config    the shared config; `config.idleMode` is read live
   * @param {object} [hooks]
   * @param {() => void} [hooks.onWake] called on the first drawn frame after a
   *   run of skipped ones, so the renderer can drop temporal history that is
   *   now stale (see RenderSystem.resetTemporal).
   */
  constructor(config, { onWake } = {}) {
    this.config = config;
    this._onWake = onWake ?? null;

    /** Frames still owed at full rate before idling is allowed. */
    this._credit = SETTLE_FRAMES;
    /** True while the simulation is frozen (mirrors `ui:pause`). */
    this._paused = false;
    /** Wall-clock ms of the last frame we let through. */
    this._lastDraw = -Infinity;
    /** Set when the previous frame was skipped, so the next one can reset. */
    this._skipped = false;
    /** True if the simulation advanced during the current run of skipped frames. */
    this._viewMoved = false;
    /** Frames skipped so far. */
    this.skippedFrames = 0;

    /**
     * True on a drawn frame whose predecessor was skipped, i.e. one whose
     * present-to-present interval measures how long we chose not to draw rather
     * than how expensive the frame was.
     *
     * The adaptive resolution governor (src/render/adaptive.js) MUST NOT sample
     * those. It already discards anything over 400 ms, which covers unpausing
     * and un-hiding, but `background` mode draws every ~100 ms — well inside the
     * window it trusts. Left unguarded, a few seconds in another application
     * would read as a machine that cannot hold 10 fps and drive the internal
     * resolution to its floor, so coming back to the game would mean coming back
     * to a soft image that then has to climb. That is exactly the kind of
     * visible downgrade this whole file is supposed to avoid.
     */
    this.metricsStale = false;

    this._bound = {
      visibility: () => {
        // Becoming visible always redraws: while hidden, the world kept
        // simulating and the presented frame is stale.
        if (!document.hidden) this.wake();
      },
      focus: () => this.wake(),
    };
    this._attached = false;
    this._unbind = [];
  }

  get mode() {
    const m = this.config?.idleMode;
    return IDLE_MODES.includes(m) ? m : 'safe';
  }

  attach() {
    if (this._attached) return;
    this._attached = true;
    document.addEventListener('visibilitychange', this._bound.visibility);
    addEventListener('focus', this._bound.focus);
  }

  detach() {
    if (!this._attached) return;
    this._attached = false;
    document.removeEventListener('visibilitychange', this._bound.visibility);
    removeEventListener('focus', this._bound.focus);
    for (const off of this._unbind) off();
    this._unbind.length = 0;
  }

  /**
   * Listen for the things that invalidate a frozen frame. Called by the engine
   * once the event bus exists.
   */
  bindEvents(events) {
    if (!events?.on) return;
    const wake = () => this.wake();

    // Pause state drives the whole `safe` mode. Track it from the event rather
    // than polling `time.scale` so the intent is explicit.
    const onPause = (e) => {
      this._paused = !!e?.paused;
      // Both directions need frames: entering pause has to settle before it
      // freezes, and leaving it has to redraw a live world immediately.
      this.wake();
    };

    // EventBus.on returns its own unsubscriber.
    this._unbind.push(
      events.on('ui:pause', onPause),
      // A resize clears the canvas, so the presented frame is gone.
      events.on('resize', wake),
      // Anything that moves pixels while the menu is open over a frozen world.
      events.on('ui:quality', wake),
      events.on('ui:fov', wake),
      events.on('ui:setting', wake)
    );
  }

  /** Force the next `frames` frames to be drawn at full rate. */
  wake(frames = WAKE_FRAMES) {
    this._credit = Math.max(this._credit, frames);
  }

  /**
   * Decide whether this frame's render call can be skipped.
   *
   * Call once per rAF tick, from the frame loop only — never from the capture
   * harness's hand-pumped `step()`, which must always draw.
   *
   * @param {number} now  the rAF timestamp, ms
   * @returns {boolean} true to skip `render.render(ctx)` for this frame
   */
  shouldSkipRender(now) {
    const reason = this._decide(now);

    if (reason) {
      this._skipped = true;
      this.skippedFrames++;
      // A run of skips only leaves the temporal state stale if the WORLD moved
      // while we were not drawing, and a frozen clock is what decides that —
      // not which test caught the frame. Hiding an already-paused tab advances
      // nothing either, so it must not force a reset on the way back.
      if (!this._paused) this._viewMoved = true;
      this.lastSkipReason = reason;
      return true;
    }

    // Flag this frame's timing as unrepresentative for the resolution governor.
    this.metricsStale = this._skipped;

    if (this._skipped) {
      this._skipped = false;
      // First drawn frame after a run of skips. The renderer's temporal state
      // (TAA history, and the previous view-projection that velocity and
      // reprojection are built from) describes the last frame it DREW. If the
      // simulation advanced since then, that is a viewpoint the world has moved
      // away from: reprojecting against it smears in motion blur and ghosts in
      // TAA, so the renderer is given a clean slate.
      //
      // Unpausing is deliberately excluded. `time.scale` was 0 for every frame
      // we skipped, so the camera, the world and therefore both the history and
      // `_prevVP` are still exactly right — and resetting anyway would throw
      // away a converged TAA history and cost a frame of SSR for nothing. The
      // most common idle exit there is stays bit-identical to never having
      // idled at all.
      if (this._viewMoved) {
        this._viewMoved = false;
        this._onWake?.();
      }
    }
    if (this._credit > 0) this._credit--;
    this._lastDraw = now;
    return false;
  }

  /**
   * @returns {'hidden'|'paused'|'background'|null} why this frame is skippable,
   *   or null to draw it.
   */
  _decide(now) {
    if (this.mode === 'off') return null;

    // Nothing is on screen. Cheapest and most clearly correct skip there is;
    // it also covers a minimised window, which some browsers keep ticking rAF
    // for at the full refresh rate.
    if (document.hidden) return 'hidden';

    // Owed full frames (just paused, just resized, just changed a setting).
    if (this._credit > 0) return null;

    // Simulation frozen and the image has settled: the frame we would draw is
    // the frame already on screen. A WebGL canvas keeps presenting its last
    // buffer until something draws again, so not drawing *is* holding the
    // image — no readback, no copy, no cost.
    if (this._paused) return 'paused';

    // Opt-in: an unfocused window still updates, just not at refresh rate.
    if (this.mode === 'background' && !document.hasFocus()) {
      const due = 1000 / BACKGROUND_FPS - DEADLINE_SLACK_MS;
      return now - this._lastDraw < due ? 'background' : null;
    }

    return null;
  }
}
